import { GetStaticProps } from 'next';
// import type { NextPage } from 'next';
// import Link from 'next/link';
// import { useRouter } from 'next/router';
import { useEffect, useState, useRef } from 'react';

import gsap from 'gsap/dist/gsap';
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin';

import { NEXTJS_REVALIDATE_PAGE_IN_SECONDS, PREVIEW_KEY } from 'siteConfig';

import { DefaultMediaBlockProps } from 'components/blocks/switchBlock/SwitchBlock';
import { MediaBlock } from 'components/blocks/mediaBlock/MediaBlock';
import { MetaDataType } from 'components/metaData/MetaData';
import { MainHeaderPlaceholder } from 'components/mainHeader/MainHeader';
import { MainFooter } from 'components/mainFooter/MainFooter';
import { Grid } from 'components/grid/Grid';

// import { useAppContext } from 'context/AppContext';
import { GlobalContentContextType, useGlobalContentContext } from 'context/GlobalContentContext';
import { getGlobalContentContext } from 'drf/getGlobalContentContext';

import { useWindowSize } from 'hooks/useWindowSize';
import { getPrivateAPI, getPublicAPI, isDesktopWidth } from 'utils/helpers';
import { widont } from 'utils/typography';

import style from './index.module.scss';

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollToPlugin);
  // gsap.config({});
}

export type AdHocBlockType = {
  title: string;
  slug: string;
  text: string;
} & DefaultMediaBlockProps;

export type AdHocPageType = {
  title: string;
  slug: string;
  b_01: AdHocBlockType;
  meta_data?: MetaDataType;
  last_modified: Date;
};

type Props = {
  page: AdHocPageType;
  meta_data: MetaDataType;
  globalContentContextData: GlobalContentContextType;
};

const AdHocPage = (props: Props) => {
  const { page, globalContentContextData } = props;
  const { setData } = useGlobalContentContext();
  const windowSize = useWindowSize();
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const [hidePageFooter, setHidePageFooter] = useState<boolean>(false);
  const pageFooterRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setData(globalContentContextData);
  }, [globalContentContextData]);

  useEffect(() => {
    const desktop = isDesktopWidth(windowSize.width);
    setIsDesktop(desktop);
  }, [windowSize.width]);

  useEffect(() => {
    // intersection observer would be nice here
    // but will go with scroll
    const pageFooterElement = pageFooterRef.current;
    if (!pageFooterElement || !isDesktop) return;
    const windowHeight = window.innerHeight;
    const onScroll = (e: any) => {
      const footerPosition =
        pageFooterElement.offsetTop - window.scrollY + pageFooterElement.clientHeight;
      if (footerPosition < windowHeight - 10) {
        // hide
        // if (!hidePageFooter)
        setHidePageFooter(true);
      } else {
        // show
        // if (hidePageFooter)
        setHidePageFooter(false);
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [pageFooterRef.current, windowSize.width, isDesktop]);

  const onAnchorClick = (e: any) => {
    gsap.to(window, {
      duration: 1.5,
      scrollTo: {
        y: `#${e.target.dataset.href}`,
        offsetY: 50,
      },
    });
  };

  return (
    <>
      <article className={`${style.container} `}>
        <MainHeaderPlaceholder />
        <Grid back={true} />

        {/* This is up top because it's sticky */}
        {isDesktop ? (
          <footer
            className={`${style.pageFooter} ${hidePageFooter ? style.hide : undefined}`}
            ref={pageFooterRef}
          >
            <Grid />
            <div className={style.phone}>+64 9 3570686​</div>
            <div className={style.name}>Moller Architects</div>
          </footer>
        ) : null}

        <header className={`${style.pageHeader}`}>
          <h1 dangerouslySetInnerHTML={{ __html: widont(page.title.replace(',', ',<br>')) }}></h1>
        </header>

        {[page.b_01].map((item, index) => {
          const media: DefaultMediaBlockProps = {
            image: item.image,
            image_height: item.image_height,
            image_width: item.image_width,
            phone_image: item.phone_image,
            phone_image_height: item.phone_image_height,
            phone_image_width: item.phone_image_width,
            alt_text: item.alt_text,
            aspect_ratio: item.aspect_ratio,
          };
          return (
            <div key={`${index}-block`}>
              <div className={style[item.slug]}>
                {item.image ? (
                  <div className={style.stickyImage}>
                    <MediaBlock
                      key={index}
                      media={media}
                      layout="responsive"
                      loading="lazy"
                      objectFit="cover"
                      block_type="media"
                      // priority={index < 3}
                      sizes="50vw"
                      phone_sizes="50vw"
                    />
                  </div>
                ) : (
                  <div>{/* placeholder */}</div>
                )}
                <div className={`${style.text}`}>
                  <div
                    className={`${style.textShift}`}
                    dangerouslySetInnerHTML={{ __html: widont(item.text) }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })}
      </article>
      <MainFooter location="footer" />
    </>
  );
};

export default AdHocPage;

/*  This function gets called at build time
    Call an external API endpoint to get data
    Get the paths we want to pre-render based on data 
    
    We'll pre-render only these paths at build time.
    { fallback: blocking } will server-render pages
    on-demand if the path doesn't exist.*/
export async function getStaticPaths() {
  const pagesRequest = await fetch(`${getPrivateAPI()}/adhocpage/`);
  const pages = await pagesRequest.json();

  const paths = pages.map((page: any, index: number) => {
    return {
      params: { slug: page.slug },
    };
  });
  return { paths, fallback: 'blocking' };
}

/* This gets called at build time, to generate the static props,
       revalidate parameter defines the cache life */
export const getStaticProps: GetStaticProps = async (context) => {
  const { preview, params } = context;

  let previewParam = '';
  if (preview) {
    previewParam = `?preview=${PREVIEW_KEY}`;
  }
  const globalContentContextData = await getGlobalContentContext(preview);
  const pageRequest = await fetch(`${getPrivateAPI()}/adhocpage/${params?.slug}/${previewParam}`);
  const page = await pageRequest.json();
  if (!page || pageRequest.status === 404) {
    return {
      notFound: true,
    };
  }

  const props: Props = {
    page: page,
    meta_data: Object.assign({}, globalContentContextData.site_meta_data, page.meta_data),
    globalContentContextData: globalContentContextData,
  };
  return {
    props: props,
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  };
};
